import {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import * as api from '../api';
import CategoryChips from '../components/analytics/CategoryChips';
import MetricBarChart from '../components/analytics/MetricBarChart';
import MetricEventCell from '../components/analytics/MetricEventCell';
import MetricLineChart from '../components/analytics/MetricLineChart';
import PeriodToolbar from '../components/analytics/PeriodToolbar';
import SalesFunnel from '../components/analytics/SalesFunnel';
import PageHeader from '../components/ui/PageHeader';
import PageCard from '../components/ui/PageCard';
import DataTable from '../components/ui/DataTable';
import FilterBar, { FilterBarActions } from '../components/ui/FilterBar';
import FormField from '../components/ui/FormField';
import Modal from '../components/ui/Modal';
import ErrorMessage from '../components/ui/ErrorMessage';
import Loader from '../components/ui/Loader';
import { formatDateTime } from '../utils/formatDate';
import {
    buildPeriodRange,
    computeSalesKpis,
    enrichSummaryItems,
    filterSummaryByCategory,
    formatMetricDetails,
    getMetricCategoryKey,
    getMetricLabel,
    getMetricTagGroups,
} from '../utils/metricTags';
import '../components/analytics/Analytics.scss';
import './CrudPage.scss';
import './MetricsPage.scss';

const PAGE_SIZE = 50;

export default function MetricsPage() {
    const salesRef = useRef(null);
    const journalRef = useRef(null);
    const [periodDays, setPeriodDays] = useState(30);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [categoryDraft, setCategoryDraft] = useState('');
    const [tagDraft, setTagDraft] = useState('');
    const [userDraft, setUserDraft] = useState('');
    const [appliedTag, setAppliedTag] = useState('');
    const [appliedUser, setAppliedUser] = useState('');
    const [chartCategory, setChartCategory] = useState('');
    const [offset, setOffset] = useState(0);
    const [tagSummary, setTagSummary] = useState([]);
    const [daySummary, setDaySummary] = useState([]);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [detailTarget, setDetailTarget] = useState(null);

    const periodRange = useMemo(
        () => buildPeriodRange(periodDays, from, to),
        [periodDays, from, to],
    );

    const enrichedSummary = useMemo(
        () => enrichSummaryItems(tagSummary),
        [tagSummary],
    );

    const salesKpis = useMemo(
        () => computeSalesKpis(tagSummary),
        [tagSummary],
    );

    const chartBarData = useMemo(() => {
        const filtered = filterSummaryByCategory(enrichedSummary, chartCategory);
        return filtered.slice(0, 15);
    }, [enrichedSummary, chartCategory]);

    const tagGroups = useMemo(() => getMetricTagGroups(), []);

    const eventOptions = useMemo(() => {
        if (!categoryDraft) {
            return tagGroups.flatMap((group) => group.tags);
        }

        const group = tagGroups.find((item) => item.category === categoryDraft);
        return group?.tags || [];
    }, [categoryDraft, tagGroups]);

    const categoryOptions = useMemo(() => [
        { value: '', label: 'Все категории' },
        ...tagGroups.map((group) => ({
            value: group.category,
            label: group.categoryLabel,
        })),
    ], [tagGroups]);

    const eventSelectOptions = useMemo(() => [
        { value: '', label: 'Все события' },
        ...eventOptions.map(({ tag, label }) => ({ value: tag, label })),
    ], [eventOptions]);

    const loadOverview = useCallback(async () => {
        const params = {
            from: periodRange.from,
            to: periodRange.to,
        };

        const [byTag, byDay] = await Promise.all([
            api.getMetricsSummary({ ...params, group_by: 'tag' }),
            api.getMetricsSummary({ ...params, group_by: 'day' }),
        ]);

        setTagSummary(byTag.items || []);
        setDaySummary(byDay.items || []);
    }, [periodRange.from, periodRange.to]);

    const loadJournal = useCallback(async () => {
        const params = {
            limit: PAGE_SIZE,
            offset,
            from: periodRange.from,
            to: periodRange.to,
        };
        if (appliedTag) params.tag = appliedTag;
        if (appliedUser) params.user = appliedUser;

        const data = await api.listMetrics(params);
        setItems(data.items || []);
        setTotal(data.total || 0);
    }, [periodRange.from, periodRange.to, appliedTag, appliedUser, offset]);

    const reload = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            await Promise.all([loadOverview(), loadJournal()]);
        } catch (err) {
            setError(err.message || 'Ошибка загрузки');
            setTagSummary([]);
            setDaySummary([]);
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [loadOverview, loadJournal]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        if (window.location.hash === '#sales') {
            window.setTimeout(() => {
                salesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 200);
        }
    }, [loading]);

    const handlePeriodChange = (days) => {
        setPeriodDays(days);
        setFrom('');
        setTo('');
        setOffset(0);
    };

    const handleDateChange = (field, value) => {
        if (field === 'from') setFrom(value);
        else setTo(value);
        setOffset(0);
    };

    const applyJournalFilters = () => {
        setAppliedTag(tagDraft);
        setAppliedUser(userDraft.trim());
        setOffset(0);
    };

    const applyTagFilter = (tag) => {
        setTagDraft(tag);
        setAppliedTag(tag);
        setCategoryDraft(getMetricCategoryKey(tag));
        setOffset(0);
        window.setTimeout(() => {
            journalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const handleCategoryDraftChange = (value) => {
        setCategoryDraft(value);
        setTagDraft('');
    };

    const columns = useMemo(() => [
        {
            key: 'created_at',
            title: 'Время',
            render: (v) => formatDateTime(v),
        },
        {
            key: 'tag',
            title: 'Событие',
            render: (v) => <MetricEventCell tag={v} />,
        },
        {
            key: 'user',
            title: 'Пользователь',
            render: (v) => v || '—',
        },
        {
            key: 'details',
            title: 'Детали',
            render: (_, row) => formatMetricDetails(row.tag, row.metadata) || '—',
        },
        {
            key: 'actions',
            title: '',
            width: 48,
            render: (_, row) => (
                <button
                    type="button"
                    className="metrics-page__details-btn"
                    title="Подробности"
                    onClick={() => setDetailTarget(row)}
                >
                    ⋯
                </button>
            ),
        },
    ], []);

    if (loading && tagSummary.length === 0 && items.length === 0) {
        return <Loader text="Загружаем аналитику..." />;
    }

    return (
        <div className="crud-page metrics-page">
            <PageHeader
                title="Аналитика"
                subtitle="Воронка продаж и действия пользователей за период"
            />

            <PeriodToolbar
                periodDays={periodDays}
                from={from}
                to={to}
                onPeriodChange={handlePeriodChange}
                onFromChange={(value) => handleDateChange('from', value)}
                onToChange={(value) => handleDateChange('to', value)}
            />

            <ErrorMessage message={error} onRetry={reload} />

            <section className="metrics-page__sales" ref={salesRef} id="sales">
                <h3 className="metrics-page__section-title">Продажи</h3>
                <div className="bento-grid bento-grid--4 metrics-page__stats">
                    <div className="stat-card">
                        <span className="stat-card__value">{salesKpis.initiated}</span>
                        <span className="stat-card__label">Начато оплат</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-card__value">{salesKpis.confirmed}</span>
                        <span className="stat-card__label">Подтверждено оплат</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-card__value">
                            {salesKpis.conversion != null ? `${salesKpis.conversion}%` : '—'}
                        </span>
                        <span className="stat-card__label">Конверсия</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-card__value">{salesKpis.errors}</span>
                        <span className="stat-card__label">Ошибки оплаты</span>
                    </div>
                </div>
                <PageCard>
                    <SalesFunnel tagSummary={tagSummary} onStepClick={applyTagFilter} />
                </PageCard>
            </section>

            <section className="metrics-page__activity">
                <h3 className="metrics-page__section-title">Активность</h3>
                <CategoryChips value={chartCategory} onChange={setChartCategory} />
                <div className="metrics-page__grid">
                    <PageCard>
                        <h4 className="metrics-page__chart-title">Популярные действия</h4>
                        <MetricBarChart
                            data={chartBarData}
                            onBarClick={(row) => row?.tag && applyTagFilter(row.tag)}
                        />
                    </PageCard>
                    <PageCard>
                        <h4 className="metrics-page__chart-title">Динамика по дням</h4>
                        <MetricLineChart data={daySummary} />
                    </PageCard>
                </div>
            </section>

            <section className="metrics-page__journal" ref={journalRef}>
                <h3 className="metrics-page__section-title">Хронология</h3>

                <FilterBar>
                    <FormField
                        inline
                        label="Категория"
                        value={categoryDraft}
                        options={categoryOptions}
                        onChange={(e) => handleCategoryDraftChange(e.target.value)}
                    />
                    <FormField
                        inline
                        label="Событие"
                        value={tagDraft}
                        options={eventSelectOptions}
                        onChange={(e) => setTagDraft(e.target.value)}
                    />
                    <FormField
                        inline
                        label="Логин"
                        value={userDraft}
                        onChange={(e) => setUserDraft(e.target.value)}
                        placeholder="demo"
                    />
                    <FilterBarActions>
                        <button type="button" className="btn btn--primary" onClick={applyJournalFilters}>
                            Применить
                        </button>
                    </FilterBarActions>
                </FilterBar>

                {appliedTag && total === 0 && !loading && (
                    <p className="metrics-page__empty">
                        Нет событий «
                        {getMetricLabel(appliedTag)}
                        » за выбранный период
                    </p>
                )}

                {!appliedTag && total === 0 && !loading && tagSummary.length === 0 && (
                    <p className="metrics-page__empty">
                        За выбранный период событий нет. Проверьте трафик или включите mock-режим для демо.
                    </p>
                )}

                <DataTable
                    columns={columns}
                    rows={items}
                    loading={loading}
                    pagination={{
                        total,
                        limit: PAGE_SIZE,
                        offset,
                        onPageChange: setOffset,
                    }}
                />
            </section>

            <Modal
                open={Boolean(detailTarget)}
                title={detailTarget ? getMetricLabel(detailTarget.tag) : ''}
                onClose={() => setDetailTarget(null)}
                wide
                footer={(
                    <button type="button" className="btn btn--outline btn--sm" onClick={() => setDetailTarget(null)}>
                        Закрыть
                    </button>
                )}
            >
                {detailTarget && (
                    <>
                        <div className="metrics-page__detail-block">
                            <span className="metrics-page__detail-label">Событие</span>
                            <span className="metrics-page__detail-value">{getMetricLabel(detailTarget.tag)}</span>
                        </div>
                        <div className="metrics-page__detail-block">
                            <span className="metrics-page__detail-label">Время</span>
                            <span className="metrics-page__detail-value">{formatDateTime(detailTarget.created_at)}</span>
                        </div>
                        <div className="metrics-page__detail-block">
                            <span className="metrics-page__detail-label">Пользователь</span>
                            <span className="metrics-page__detail-value">{detailTarget.user || '—'}</span>
                        </div>
                        <div className="metrics-page__detail-block">
                            <span className="metrics-page__detail-label">Детали</span>
                            <span className="metrics-page__detail-value">
                                {formatMetricDetails(detailTarget.tag, detailTarget.metadata) || '—'}
                            </span>
                        </div>
                        <details className="metrics-page__dev-details">
                            <summary>Для разработчика</summary>
                            <div className="metrics-page__detail-block">
                                <span className="metrics-page__detail-label">Системный код</span>
                                <span className="metrics-page__detail-value metrics-page__detail-value--mono">
                                    {detailTarget.tag}
                                </span>
                            </div>
                            <div className="metrics-page__detail-block">
                                <span className="metrics-page__detail-label">Visitor ID</span>
                                <span className="metrics-page__detail-value metrics-page__detail-value--mono">
                                    {detailTarget.visitor_id || '—'}
                                </span>
                            </div>
                            <div className="metrics-page__detail-block">
                                <span className="metrics-page__detail-label">JSON</span>
                                <pre className="metrics-page__detail-pre">
                                    {detailTarget.metadata
                                        ? JSON.stringify(detailTarget.metadata, null, 2)
                                        : '—'}
                                </pre>
                            </div>
                        </details>
                    </>
                )}
            </Modal>
        </div>
    );
}
